/**
 * Device signals and the payload that rides along with them (BOAT-1895).
 *
 * Two ways the device reaches the API, and the split is the point:
 *
 *  - {@link devicePayload} is merged into a request that was being sent anyway
 *    — an order, a login, a cart, a signup. Those already carry an email, so
 *    they are the ones that can link a device to a person.
 *  - {@link sendDeviceSignal} is for the moments with no request of their own:
 *    the first landing, an add to cart, the start of a checkout.
 *
 * WRITES ARE SIGNAL-DRIVEN, NOT PER PAGE VIEW. That is what keeps the table a
 * few rows per visitor instead of a few hundred, and it is why there is no
 * router hook here: a page view is not a signal.
 *
 * `landing` no longer travels on its own (BOAT-1897). The visit beacon takes
 * the same device block, so a landing that sent both would be two requests
 * writing one device row; it now rides along with the arrival it describes.
 * The signal name is kept because the API still whitelists it and a caller
 * with no visit to record may legitimately want it.
 */

import { recordDeviceSignal } from '~/queries'

export const deviceSignals = [
  'landing',
  'form_submit',
  'add_to_cart',
  'checkout_start',
  'login',
  'order',
] as const

export type DeviceSignal = (typeof deviceSignals)[number]

/**
 * Matches the API's own `last_seen_at` throttle: inside the window the same
 * signal changes nothing server-side, so sending it again is pure noise.
 */
const SIGNAL_THROTTLE_MS = 60 * 60 * 1000
const SIGNAL_STORAGE_PREFIX = 'tc_device_signal_'

/** Longer than any healthy browser needs, far shorter than a customer notices. */
const DEVICE_PAYLOAD_DEADLINE_MS = 1500

export type DeviceBlock = {
  visitor_id: string
  components?: Record<string, unknown>
}

/** Either a device block or nothing to send - never a partial. */
export type DevicePayload = { device?: DeviceBlock }

const withDeadline = <T>(work: Promise<T>, ms: number, fallback: T) =>
  new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms)

    work
      .then((value) => resolve(value), () => resolve(fallback))
      .finally(() => clearTimeout(timer))
  })

const readNumber = (key: string) => {
  try {
    const value = window.localStorage.getItem(key)

    return value === null ? null : Number.parseInt(value, 10)
  } catch {
    return null
  }
}

const writeNumber = (key: string, value: number) => {
  try {
    window.localStorage.setItem(key, String(value))
  } catch {
    // A visitor with storage blocked simply sends the signal every time; the
    // API's own throttle still stops it turning into a write.
  }
}

/**
 * The high-entropy client hints, read from the browser rather than waited for
 * in a header.
 *
 * Chrome froze the user-agent string, so the model and the real platform
 * version only exist here. The header route (`Accept-CH`) needs the document to
 * delegate the hints to the API's origin, and Safari ignores the whole
 * mechanism — so this is the path that actually works, and the headers are the
 * bonus.
 */
const clientHints = async () => {
  const uaData = (navigator as any).userAgentData

  if (!uaData?.getHighEntropyValues) return undefined

  try {
    const hints = await uaData.getHighEntropyValues([
      'model',
      'platformVersion',
      'fullVersionList',
    ])

    return {
      platform: hints.platform ?? uaData.platform,
      platform_version: hints.platformVersion,
      model: hints.model,
      full_version_list: hints.fullVersionList,
      mobile: uaData.mobile,
    }
  } catch {
    return undefined
  }
}

/**
 * The signature components.
 *
 * Deliberately only values that belong to the DEVICE and not to the visit: the
 * window size, the scroll position and the page would change on every request
 * and turn a signature meant to survive a cleared cookie into a new row per
 * page view.
 *
 * This is a LOW-CONFIDENCE hint and nothing more — thousands of identical
 * handsets produce an identical hash, so the API keys identity on the visitor
 * id and lets the fingerprint corroborate.
 */
const deviceComponents = async () => {
  const components: Record<string, unknown> = {}

  try {
    components.screen = `${screen.width}x${screen.height}x${screen.colorDepth}`
    components.pixel_ratio = window.devicePixelRatio
    components.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    components.language = navigator.language
    components.languages = (navigator.languages ?? []).join(',')
    components.platform = (navigator as any).platform
    components.hardware_concurrency = navigator.hardwareConcurrency
    components.device_memory = (navigator as any).deviceMemory
    components.touch_points = navigator.maxTouchPoints
  } catch {
    // Partial components still hash; an empty set simply records no signature.
  }

  const hints = await clientHints()

  if (hints) components.client_hints = hints

  return components
}

export const useDeviceSignal = () => {
  const { resolveVisitorId } = useVisitorId()

  /**
   * The per-site flag, read from the site settings the storefront already has.
   *
   * Checked BEFORE minting, not after sending: a site with capture switched off
   * should never have an id written into its visitors' browsers in the first
   * place. The API enforces the same flag — this just stops us asking.
   */
  const isEnabled = () => {
    if (import.meta.server) return false

    try {
      return useSiteStore().data?.deviceTrackingEnabled !== false
    } catch {
      // Read lazily, so a call site outside a store context (a plugin, a
      // handler resumed after an await) cannot throw. The API applies the same
      // flag, so falling through here costs a request, not a leak.
      return true
    }
  }

  /**
   * The device block for a request that carries an email, in the shape the API
   * accepts. Returns an empty object when there is nothing to send, so it
   * spreads into a body the same way `utmPayload()` does.
   */
  const devicePayload = async (): Promise<DevicePayload> => {
    if (!isEnabled()) return {}

    // Bounded, because this is awaited on the order, login and register
    // requests. Resolving the id opens IndexedDB and reading the hints calls
    // into the UA-data API, and either can stall in a private window or an
    // odd browser without ever settling. A checkout must not wait on
    // telemetry: past the deadline the request goes out without a device
    // block, which the API treats as "nothing to record".
    const payload = await withDeadline(
      (async () => {
        const visitorId = await resolveVisitorId()

        if (!visitorId) return {}

        return {
          device: {
            visitor_id: visitorId,
            components: await deviceComponents(),
          },
        } satisfies DevicePayload
      })(),
      DEVICE_PAYLOAD_DEADLINE_MS,
      {},
    )

    return payload
  }

  /**
   * A signal with no request of its own to travel on.
   *
   * Never rejects: the visitor is shopping, and an analytics call is not
   * allowed to interrupt that.
   */
  const sendDeviceSignal = async (signal: DeviceSignal) => {
    if (!isEnabled()) return

    const storageKey = `${SIGNAL_STORAGE_PREFIX}${signal}`
    const lastSentAt = readNumber(storageKey)
    const now = Date.now()

    if (lastSentAt !== null && now - lastSentAt < SIGNAL_THROTTLE_MS) return

    const payload = await devicePayload()

    if (!payload.device) return

    writeNumber(storageKey, now)

    try {
      await recordDeviceSignal({ signal, device: payload.device })
    } catch {
      // Let the next signal try again rather than surface anything.
      writeNumber(storageKey, 0)
    }
  }

  return { devicePayload, sendDeviceSignal }
}
